import React, { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import {
  updateCamera,
  addCamera,
  CameraEntity,
  getCamera,
  IconData,
  useEntityTypeCamera,
  ApiResponse,
} from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import Details from "components/camera/AddEdit/Details";
import Location from "components/camera/AddEdit/Location";
import CameraProperties from "components/camera/AddEdit/Properties";
import CameraType from "components/camera/AddEdit/CameraType";
import Icon from "components/common/Icon";
import { makeStyles } from "@material-ui/core";
import OverflowTooltip from "@hitachivantara/uikit-react-core/dist/OverflowTooltip";
import { UserDataContext } from "lib/context/UserDataContext";
import { UserPolicyTypes } from "lib/api/userSettings";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";
import {
  cameraFromToCameraEntity,
  useCameraInitialValues,
  useCameraValidation,
} from "../Common";

const Add: React.FC = () => {
  const { t } = useTranslation("cameraAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { data: cameraType } = useEntityTypeCamera({ suspense: true });
  const [saveAndStay, setSaveAndStay] = useState(true);
  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);
  const [camera, setCamera] = useState<CameraEntity | undefined>(undefined);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const { hasSystemTypePolicy } = useContext(UserDataContext);
  const sections = [
    { label: t("details.title"), value: "details" },
    { label: t("cameraType.title"), value: "type" },
    { label: t("location.title"), value: "location" },
    { label: t("properties.title"), value: "properties" },
  ];

  useEffect(() => {
    const isAuthorized = hasSystemTypePolicy(
      cameraType?.data.id ?? "",
      UserPolicyTypes.insertupdate
    );
    if (!isAuthorized) {
      history.push("/settings/camera");
    }
  }, [cameraType?.data.id, hasSystemTypePolicy, history]);

  const validationSchema = useCameraValidation(t);
  const initialValues = useCameraInitialValues(
    camera,
    cameraType?.data,
    undefined,
    undefined,
    undefined,
    hasSystemTypePolicy
  );

  const onSave = async (data: CameraFormData) => {
    const cameraNewData = cameraFromToCameraEntity(data, camera, initialValues);
    let id = camera?.id ?? "";
    if (!camera) {
      const cameraData = await addCamera(cameraType?.data.id ?? "", {
        ...cameraNewData,
        create_timestamp: new Date(),
      });
      id = (cameraData.data as ApiResponse<CameraEntity>).data.id;
    } else {
      await updateCamera(camera.id, cameraType?.data.id ?? "", {
        ...camera,
        ...cameraNewData,
      });
    }
    try {
      const newcameraData = await getCamera(id, cameraType?.data.id ?? "");
      setCamera(newcameraData.data.data);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  };

  /* const jumpToReleventHtmlId = (id: string) => {
    const releventDiv = document.getElementById(id);
    // behavior: "smooth" parameter for smooth movement
    releventDiv?.scrollTo({ top: 0 });
  }; */
  const onSubmitHandler = async (
    values: CameraFormData,
    actions: FormikHelpers<CameraFormData>
  ) => {
    try {
      await onSave(values);
      if (camera) {
        enqueueSnackbar(t("validation.success.update"), { variant: "success" });
      } else {
        enqueueSnackbar(t("validation.success.add"), { variant: "success" });
      }
      setPreventNavigation(false);
      if (!saveAndStay) {
        history.push("/settings/camera");
      } else {
        setMode("edit");
        actions.setStatus({});
      }
      // actions.setStatus({});
    } catch (error) {
      setShowBanner(true);
    }
  };
  const innerClasses = useMemo(
    () =>
      makeStyles(() => ({
        background: {
          backgroundColor: cameraType?.data.appearance?.color,
          "& svg": {
            color: "white",
          },
        },
      })),
    [cameraType?.data.appearance]
  )();

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as CameraFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
      enableReinitialize
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.push(`/settings/camera/`);
          // history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        return (
          <Container
            className={classes.container}
            maxWidth="xl"
            key="cameraAddEditContainer"
          >
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={
                camera
                  ? t("validation.unknownError.edit")
                  : t("validation.unknownError.add")
              }
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={
                  <>
                    <div
                      className={clsx(
                        classes.formHeaderIcon,
                        innerClasses.background
                      )}
                    >
                      <Icon
                        iconData={
                          (cameraType?.data.appearance as IconData) || {}
                        }
                        {...(cameraType?.data.appearance &&
                          cameraType?.data.appearance?.icon_type ===
                            "fontawesome" && {
                            color: "#FFFFFF",
                          })}
                      />
                    </div>
                    <OverflowTooltip
                      data={(camera?.name as string) ?? t("title")}
                      className={clsx(classes.title)}
                    />
                  </>
                }
                sections={sections}
                onCancel={goBackHandler}
                saveExitVisible
                onSaveExit={() => {
                  setSaveAndStay(false);
                  handleSubmit();
                }}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={clsx(
                    classes.sectionMargin,
                    classes.sectionMarginOverride
                  )}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details mode={mode} />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <CameraType type={initialValues.type} mode={mode} />
                </HvGrid>
                <HvGrid
                  className={clsx(
                    classes.sectionMargin,
                    classes.sectionMarginOverride
                  )}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <Location />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[3].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <CameraProperties {...arrayHelpers} />
                    )}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const CameraAddAuth: React.FC = () => {
  const { hasSystemTypeNamePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return (
      hasSystemTypeNamePolicy("camera", UserPolicyTypes.enable) &&
      hasSystemTypeNamePolicy("camera", UserPolicyTypes.insertupdate)
    );
  }, [hasSystemTypeNamePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Add />{" "}
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Add;
